import { SvgIcon } from '@bob/library'
import { useLocale } from '@websrc/App/Hooks/useLocale'
import { CTAType, mapCTA } from '@websrc/DataMappers/mapCTA'
import clsx from 'clsx'
import { Link } from 'gatsby'
import { button, buttonDefaultPrefix, ButtonVariants } from 'leds'
import React, { FC, forwardRef, ReactNode, Ref } from 'react'
import { match } from 'ts-pattern'

import IconProvider from '../../../coral-library/icons/IconProvider'
import { MtmParams, useMTMParams } from '../Hooks/useMTMParams'

export type CTAProps = ButtonVariants & {
  cta: ReturnType<typeof mapCTA>
  onClick?: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void
  prefix?: ReactNode
  suffix?: ReactNode
  noText?: boolean
  withDefaultPrefix?: boolean
  className?: string
  children?: React.ReactNode
  iconName?: string
  isFocuseable?: boolean
  iconDirection?: 'up' | 'down' | 'left' | 'right'
}

const CtaDefaultPrefix: FC<{ colorTheme: CTAProps['colorTheme']; type: string | undefined }> = ({
  colorTheme = 'blue',
  type,
}) => {
  const color = match<typeof colorTheme, Parameters<typeof SvgIcon>[0]['color']>(colorTheme)
    .with('blue', () => 'white') // blue colorTheme means white icon and blue background
    .with('blueInverse', () =>
      type === 'primary' ? 'white' : type === 'secondary' ? 'black' : 'blue6'
    ) // blueInverse colorTheme means blue icon and white background
    .with('black', () => 'white')
    .with('blackInverse', () => 'black')
    .exhaustive()

  return (
    <SvgIcon
      className={buttonDefaultPrefix}
      color={color}
      name={'ARROW_RIGHT'}
      size={14}
      strokeWidth={8}
    />
  )
}

export const ctaColorTheme = (type: CTAType | undefined) => {
  switch (type) {
    case 'primary':
      return 'blue'

    case 'secondary':
      return 'blueInverse'

    default:
      return undefined
  }
}

type NewTabCTA = Pick<HTMLAnchorElement, 'target' | 'rel'> | {}

export const CTA = forwardRef<HTMLAnchorElement, CTAProps>(
  (
    {
      prefix,
      suffix,
      children,
      cta,
      type,
      colorTheme,
      fontWeight,
      fontSize,
      withDefaultPrefix,
      noText,
      className,
      isFocuseable = true,
      iconName,
      iconDirection = 'down',
      onClick,
      ...rest
    },
    ref
  ) => {
    const locale = useLocale()

    const newTab: NewTabCTA = cta?.newTab ? { target: '_blank', rel: 'noreferrer noopener' } : {}
    const buttonClass = clsx(button({ type, colorTheme, fontWeight, fontSize }), className)
    const combination_of_mtm_params = cta?.combination_of_mtm_params
    const mtm_params = cta?.mtm_params as MtmParams
    const resultMTMs = useMTMParams(combination_of_mtm_params, mtm_params)
    if (!iconName && cta?.__ctaType === 'external' && cta?.newTab) {
      iconName = 'upRightArrow'
    }
    if (!prefix && !suffix && withDefaultPrefix) {
      prefix = <CtaDefaultPrefix colorTheme={colorTheme} type={type} />
    }

    const LinkContainer = ({ children }: { children: React.ReactNode }) =>
      match(cta)
        .with({ __ctaType: 'internal' }, (data) => (
          <Link
            {...newTab}
            className={buttonClass}
            onClick={onClick}
            tabIndex={isFocuseable ? 0 : -1}
            to={`/${locale}${data.url}${resultMTMs}${data.internal_link_section}`}
            {...rest}
            data-t-id={data.trackingId}
            ref={ref as Ref<any>}
          >
            {children}
          </Link>
        ))
        .with({ __ctaType: 'external' }, (data) => (
          <a
            {...newTab}
            className={buttonClass}
            href={`${data.url}${resultMTMs}`}
            onClick={onClick}
            tabIndex={isFocuseable ? 0 : -1}
            {...rest}
            data-t-id={data.trackingId}
            ref={ref as Ref<HTMLAnchorElement>}
          >
            {children}
          </a>
        ))
        .with({ __ctaType: 'file' }, (data) => (
          <a
            {...newTab}
            className={buttonClass}
            href={`${data.file?.url}`}
            onClick={onClick}
            tabIndex={isFocuseable ? 0 : -1}
            {...rest}
            data-t-id={data.trackingId}
            ref={ref as Ref<HTMLAnchorElement>}
          >
            {children}
          </a>
        ))
        .with({ __ctaType: 'modal' }, (data) => (
          <a
            className={buttonClass}
            data-modal-id={data.modal_id}
            href={''}
            onClick={(e) => {
              e.preventDefault()
              onClick
            }}
            tabIndex={isFocuseable ? 0 : -1}
            {...rest}
            data-t-id={data.trackingId}
            ref={ref as Ref<HTMLAnchorElement>}
          >
            {children}
          </a>
        ))
        .otherwise(() => null)
    return (
      <LinkContainer>
        {prefix}
        {!noText && cta?.text}
        {noText && children}
        {iconName && (
          <IconProvider
            className={'coral-global-button__icon'}
            direction={iconDirection}
            name={iconName}
          />
        )}
        {suffix}
      </LinkContainer>
    )
  }
)
